import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { I18n as I } from 'react-redux-i18n';

import { PartTypes, isNil } from 'vdlib';

import { usePartyFlags } from 'lib/Hooks';
import AuctionBidsTable from './AuctionBidsTable';
import BidSelectorWizard from './BidSelectorWizard';
const getPropertySorter = () => {};
const partTypeConfig = {
  [PartTypes.New]: {
    color: 'blue',
    label: I.t('PART_TYPE_NEW'),
    plural: I.t('PART_TYPE_NEW_PL'),
  },
  [PartTypes.Imitation]: {
    color: 'yellow',
    label: I.t('PART_TYPE_IMITATION'),
    plural: 'Imitation',
  },
  [PartTypes.Used]: {
    color: 'red',
    label: I.t('PART_TYPE_USED'),
    plural: I.t('PART_TYPE_USED_PL'),
  },
};

const AuctionBids = ({
  auctionPartPrice,
  auctionStatus,
  bids,
  imitationCategories,
  onCancel,
  onOffer,
  onSelectBid,
  onTempSelectBid,
  partDescription,
  partTypes,
  partyTypes,
  submitting,
  usedConditionTypes,
  user,
}) => {
  const scrollRef = React.useRef(null);
  const [selectedBid, setSelectedBid] = React.useState(null);
  const [selectedPartTypeID, setSelectedPartTypeID] = React.useState(null);

  const { isExpert, isInsurance, isMerchant, isOperator, partyType } =
    usePartyFlags(user, partyTypes);

  const partBids = React.useMemo(() => {
    if (!Array.isArray(bids) || !Array.isArray(partTypes)) return null;
    const amountSorter = getPropertySorter('Amount');

    return partTypes.reduce((obj, pt) => {
      obj[pt.ID] = bids.filter(b => b.PartTypeID === pt.ID).sort(amountSorter);
      return obj;
    }, {});
  }, [bids, partTypes]);

  const handleExpandGrid = React.useCallback(
    partTypeID => {
      setSelectedPartTypeID(partTypeID);

      if (!isNil(partTypeID) && scrollRef.current)
        setTimeout(() =>
          ReactDOM.findDOMNode(scrollRef.current).scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          })
        );
    },
    [setSelectedPartTypeID]
  );

  const renderActions = React.useCallback(
    (bid, partType) => {
      if (isInsurance) {
        return !bid ? (
          <div className="price-table-footer">&nbsp;</div>
        ) : (
          <div className="price-table-footer">
            <button
              type="button"
              className="btn grey-salsa btn-outline sbold uppercase price-button mr-sm"
              onClick={e => setSelectedBid(bid)}
            >
              {I.t('SELECT_C')}
            </button>
            {selectedPartTypeID === bid.PartTypeID ? (
              <button
                type="button"
                className="btn grey-salsa btn-outline sbold uppercase price-button"
                onClick={e => handleExpandGrid()}
              >
                {I.t('HIDE_C')}
              </button>
            ) : (
              <button
                type="button"
                className="btn grey-salsa btn-outline sbold uppercase price-button"
                onClick={e => handleExpandGrid(bid.PartTypeID)}
              >
                {I.t('VIEW_ALL_C')}
              </button>
            )}
          </div>
        );
      }
      if (isOperator || isExpert) {
        return !bid ? (
          <div className="price-table-footer">&nbsp;</div>
        ) : (
          <div className="price-table-footer">
            {selectedPartTypeID === bid.PartTypeID ? (
              <button
                type="button"
                className="btn grey-salsa btn-outline sbold uppercase price-button"
                onClick={e => handleExpandGrid()}
              >
                {I.t('HIDE_C')}
              </button>
            ) : (
              <button
                type="button"
                className="btn grey-salsa btn-outline sbold uppercase price-button"
                onClick={e => handleExpandGrid(bid.PartTypeID)}
              >
                {I.t('VIEW_ALL_C')}
              </button>
            )}
          </div>
        );
      }

      return !bid ? (
        <div className="price-table-footer">
          <button
            type="button"
            className="btn grey-salsa btn-outline sbold uppercase price-button"
            onClick={e => onOffer(partType)}
          >
            {I.t('OFFER_C')}
          </button>
        </div>
      ) : (
        <div className="price-table-footer">
          <button
            type="button"
            className="btn grey-salsa btn-outline sbold uppercase price-button mr-sm"
            onClick={e => onOffer(partType)}
          >
            {I.t('OFFER_NEW_C')}
          </button>
          <button
            type="button"
            className="btn grey-salsa btn-outline sbold uppercase price-button"
            onClick={e => onCancel(bid.ID)}
          >
            {I.t('CANCEL_C')}
          </button>
        </div>
      );
    },
    [
      setSelectedBid,
      selectedPartTypeID,
      isInsurance,
      isOperator,
      isExpert,
      onOffer,
      handleExpandGrid,
      onCancel,
    ]
  );

  return (
    <div className="row pricing-content-1">
      {partBids &&
        partTypes.map(partType => {
          const [bid] = partBids[partType.ID];
          const config = partTypeConfig[partType.Description];

          return (
            <div className="col-md-4" key={partType.Description}>
              <div className="price-column-container border-active">
                <div className={`price-table-head bg-${config.color}`}>
                  <h2 className="no-margin">{config.label}</h2>
                </div>
                <div className={`arrow-down border-top-${config.color}`} />
                <div className="price-table-pricing">
                  <h3>
                    {!bid ? (
                      <span style={{ fontSize: '80%' }}>{I.t('NO_OFFER')}</span>
                    ) : (
                      <span>
                        <sup className="price-sign">€</sup>
                        {bid.Amount}
                      </span>
                    )}
                  </h3>
                </div>
                {renderActions(bid, partType, config)}
              </div>
            </div>
          );
        })}

      {!isMerchant && !!selectedPartTypeID && (
        <div className="clear col-md-12 mt">
          <AuctionBidsTable
            bids={partBids[selectedPartTypeID]}
            auctionStatus={auctionStatus}
            partyType={partyType}
            partyTypes={partyTypes}
            auctionPartPrice={auctionPartPrice}
            loading={submitting}
            onSelectBid={onSelectBid}
            onTempSelectBid={onTempSelectBid}
            partDescription={partDescription}
            imitationCategories={imitationCategories}
            usedConditionTypes={usedConditionTypes}
            user={user}
          />
        </div>
      )}

      <BidSelectorWizard
        show={!!selectedBid}
        onSubmit={onSelectBid}
        loading={submitting}
        bid={selectedBid}
        onHide={() => setSelectedBid(null)}
        partDescription={partDescription}
      />

      <div className="clear" ref={elem => (scrollRef.current = elem)} />
    </div>
  );
};

AuctionBids.propTypes = {
  bids: PropTypes.array,
  partTypes: PropTypes.array.isRequired,
  submitting: PropTypes.bool,
  user: PropTypes.object,

  onOffer: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSelectBid: PropTypes.func.isRequired,
  onTempSelectBid: PropTypes.func.isRequired,
};

export default AuctionBids;
